// =============================================================================
// ChartRenderer — renders a server-produced visualise spec + data (R42).
//
// Pure presentational: given the { spec, data } from /api/agent/visualise, it
// switches on spec.chart_type and draws the matching Recharts chart. It never
// computes a figure — every number is already in `data` (the server aggregated
// it from the same live data the dashboard uses). Reuses the Recharts idiom from
// pages/dashboard/DashboardNow.tsx so on-demand charts match the fixed dashboards.
//
// Colour: the dataviz validated categorical palette (CVD worst-adjacent ΔE 24.2
// on the white chart surface; sub-3:1 slots are relieved by the always-present
// Legend + on-chart value labels). Hues are assigned by series/slice position in
// fixed order; the server caps series at MAX_SERIES (8) = the palette length, so a
// 9th would wrap to the first hue (modulo) — the cap keeps that from happening.
//
// Value labels render on the marks (not just the hover tooltip) so a saved PNG is
// readable offline. The low-cardinality delivery charts here (≤~15 categories,
// ≤8 series) stay legible; Recharts drops overlapping labels automatically.
// =============================================================================
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, LabelList,
} from 'recharts'
import type { VisualiseSpec } from '../lib/types'

// Value labels are drawn onto the chart (not just the hover tooltip) so a saved PNG is readable
// offline. Kept small and muted to stay legible without cluttering. Shared style for bar/line/area.
const LABEL_STYLE = { fontSize: 10, fill: '#475569' }

// Value-label formatter, single-sourced across every chart type so behaviour can't drift: hide 0 so
// empty categories/slices don't print a noisy "0".
const labelValue = (v: number | string) => (Number(v) === 0 ? '' : `${v}`)

// Stacked-bar segment label: only drawn when the segment is tall enough to hold the number, so thin
// segments don't overflow / collide (the tooltip still shows every value on screen). Recharts passes
// the segment's pixel geometry, so this is scale-independent.
// Only draw when the segment is at least this tall on screen — fit depends on rendered pixel
// height (value ÷ y-scale × chart height), not the raw value, so this is scale-independent.
const MIN_SEGMENT_PX = 16
function StackedLabel(props: { x?: number | string; y?: number | string; width?: number | string; height?: number | string; value?: number | string }) {
  const x = Number(props.x), y = Number(props.y), width = Number(props.width), height = Number(props.height)
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height) || height < MIN_SEGMENT_PX) return null
  return (
    <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central" fontSize={10} fill="#ffffff">
      {props.value}
    </text>
  )
}

// dataviz reference categorical palette (validated on #ffffff surface). Fixed order.
const PALETTE = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834']
const color = (i: number) => PALETTE[i % PALETTE.length]

type ChartData = Record<string, string | number>[]

// RAG tokens keep their semantic colour AND friendly label wherever they appear (pie slice, bar
// category, or split series) — Green reads green; Blue shows as "Migration Activities Completed";
// "unscheduled" shows as "Migration Date Pending". Both maps come from meta.ragBands (the RAG
// authority the fixed dashboards use, via displayByToken/colorByToken) so charts and dashboards can't
// diverge in colour or wording. Non-RAG series use the generic palette + their own value as the label.
export function ChartRenderer({ spec, data, ragColors = {}, ragLabels = {} }: { spec: VisualiseSpec; data: ChartData; ragColors?: Record<string, string>; ragLabels?: Record<string, string> }) {
  const { chart_type, x_key, series_keys } = spec
  const isPie = chart_type === 'pie' || chart_type === 'donut'
  // Colour a mark by its own token first (so RAG tokens self-colour), else palette by slot index.
  const markColor = (token: string, i: number) => ragColors[token] ?? color(i)
  // Human-facing text for a token: its RAG friendly label if it is one, else the token verbatim.
  const displayLabel = (token: string) => ragLabels[token] ?? token

  // A lone unnamed count series ('value') carries no identity of its own: the x-axis categories (or
  // the title) name it, so there's no legend and each mark self-colours by its category.
  const singleSeries = series_keys.length === 1 && series_keys[0] === 'value'
  const showLegend = !singleSeries

  return (
    <ResponsiveContainer width="100%" height={340}>
      {isPie ? (
        <PieChart>
          <Pie
            data={data}
            dataKey={series_keys[0]}
            nameKey={x_key}
            cx="50%"
            cy="50%"
            innerRadius={chart_type === 'donut' ? 70 : 0}
            outerRadius={110}
            paddingAngle={2}
            label={({ value }: { value: number }) => labelValue(value)}
            labelLine={false}
          >
            {data.map((row, i) => <Cell key={i} fill={markColor(String(row[x_key]), i)} />)}
          </Pie>
          <Tooltip formatter={(value, name) => [value, displayLabel(String(name))]} />
          <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => displayLabel(String(value))} />
        </PieChart>
      ) : chart_type === 'line' ? (
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x_key} tick={{ fontSize: 10 }} tickFormatter={displayLabel} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(value, name) => [value, displayLabel(String(name))]} labelFormatter={displayLabel} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => displayLabel(String(value))} />}
          {series_keys.map((k, i) => (
            <Line key={k} type="monotone" dataKey={k} name={displayLabel(k)} stroke={markColor(k, i)} strokeWidth={2} dot={false}>
              <LabelList dataKey={k} position="top" style={LABEL_STYLE} formatter={labelValue} />
            </Line>
          ))}
        </LineChart>
      ) : chart_type === 'area' ? (
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x_key} tick={{ fontSize: 10 }} tickFormatter={displayLabel} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(value, name) => [value, displayLabel(String(name))]} labelFormatter={displayLabel} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => displayLabel(String(value))} />}
          {series_keys.map((k, i) => (
            <Area key={k} type="monotone" dataKey={k} name={displayLabel(k)} stroke={markColor(k, i)} fill={markColor(k, i)} fillOpacity={0.15}>
              <LabelList dataKey={k} position="top" style={LABEL_STYLE} formatter={labelValue} />
            </Area>
          ))}
        </AreaChart>
      ) : (
        // bar | stacked_bar | grouped_bar
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x_key} tick={{ fontSize: 10 }} tickFormatter={displayLabel} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(value, name) => [value, displayLabel(String(name))]} labelFormatter={displayLabel} />
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(value) => displayLabel(String(value))} />}
          {series_keys.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              name={displayLabel(k)}
              fill={markColor(k, i)}
              radius={[4, 4, 0, 0]}
              stackId={chart_type === 'stacked_bar' ? 'a' : undefined}
            >
              {/* Single-series bars self-colour per category (so RAG/size bars are distinct). */}
              {singleSeries && data.map((row, ci) => <Cell key={ci} fill={markColor(String(row[x_key]), ci)} />)}
              {/* Value labels: on top for single/grouped; for stacked, inside each segment but only
                  where it's tall enough to fit (StackedLabel), so thin segments don't overflow. */}
              {chart_type === 'stacked_bar'
                ? <LabelList dataKey={k} content={StackedLabel} />
                : <LabelList dataKey={k} position="top" style={LABEL_STYLE} formatter={labelValue} />}
            </Bar>
          ))}
        </BarChart>
      )}
    </ResponsiveContainer>
  )
}
